import { yupResolver } from '@hookform/resolvers/yup/dist/yup.js'
import { Box, Button, FormControl, InputLabel, MenuItem, Select, TextField, Typography } from '@mui/material'
import { type CreateNorwegianDirectDebitAgreement, DirectDebitType } from 'api/directDebitMandate/mappers'
import { useSignatories } from 'api/react-query'
import {
  type BackOfficeAgreementSsnV8,
  BackOfficePeriodV8,
  type BackOfficeSignatoryDto,
  type NationalityV8,
  type SSN,
} from 'api/swagger/definitions/backoffice'
import { BorderContainer, prettifyEnumLabel } from 'helpers/CreditOnCardHelpers'
import { useForm } from 'react-hook-form'
import * as Yup from 'yup'

interface DirectDebitTabNewConnection {
  onCreateMandate: (data: CreateNorwegianDirectDebitAgreement) => Promise<unknown>
  isLoading?: boolean
  customerId: string
}

interface DirectDebitTabNewConnectionFormValues {
  noBankAccount: string
  signer: string
  amount: number
  period: BackOfficePeriodV8
}

const validationSchema = (signatories: BackOfficeSignatoryDto[]) =>
  Yup.object().shape({
    noBankAccount: Yup.string().required('Bank account number is required'),
    signer: Yup.string()
      .required('Signer is required')
      .test('valid-signer', 'Signer must be one of the available signatories', (value) => {
        return signatories.some((s) => s.email === value)
      })
      .test('valid-signer-ssn', 'Signer must be one of the available signatories', (value) => {
        const signatory = signatories.find((s) => s.email === value)

        return Boolean(signatory?.ssn?.identifier)
      }),
    period: Yup.string()
      .required('Period is required')
      .oneOf(Object.values(BackOfficePeriodV8), 'Period must be one of ' + Object.values(BackOfficePeriodV8).join(', ')),
    amount: Yup.number().required('Amount is required'),
  })

const isValidSignatorySSN = (signatorySSN: SSN | undefined): null | BackOfficeAgreementSsnV8 => {
  if (!signatorySSN) return null

  const ssn = signatorySSN.identifier

  if (!ssn) return null

  return {
    ssn: ssn,
    nationality: signatorySSN.country as NationalityV8,
  }
}

export const CreateMandateNorwegianBankAccount = ({ onCreateMandate, isLoading, customerId }: DirectDebitTabNewConnection) => {
  const { data: signatories } = useSignatories(customerId)

  const handleSubmit = (data: DirectDebitTabNewConnectionFormValues) => {
    const signer = signatories?.signatories?.find((s) => s.email === data.signer)
    const ssn = isValidSignatorySSN(signer?.ssn)

    if (!signer || !ssn) return

    const mandate: CreateNorwegianDirectDebitAgreement = {
      type: DirectDebitType.DIRECT_DEBIT_NORWAY,
      noBankAccount: data.noBankAccount,
      signer: {
        email: signer.email,
        fullName: signer.fullName,
        phoneNumber: signer.phoneNumber,
        ssn: signer.ssn,
      },
      amount: data.amount,
      period: data.period,
    }

    onCreateMandate(mandate).then(() => {
      form.reset()
    })
  }
  const form = useForm<DirectDebitTabNewConnectionFormValues>({
    defaultValues: {
      noBankAccount: '',
      signer: '',
      amount: 0,
      period: BackOfficePeriodV8.WEEKLY,
    },
    resolver: yupResolver(validationSchema(signatories?.signatories ?? [])),
  })

  const signatoryOptions = signatories?.signatories?.map((s) => ({ label: s.fullName + ' (' + s.email + ')', value: s.email })) ?? []

  return (
    <BorderContainer aria-label="bank-account" gap="16px">
      <Box display="flex" flexDirection="column" gap={3}>
        <Typography variant="body1" color="text.primary" fontWeight={600}>
          Add bank account number
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Set the customers bank account number and choose signatory for direct debit
        </Typography>
      </Box>
      <Box display="flex" flexDirection="column" gap={2}>
        <TextField aria-label="noBankAccount" {...form.register('noBankAccount')} label="Bank account number" />
        <FormControl>
          <InputLabel id="Signer">Signer</InputLabel>
          <Select
            {...form.register('signer')}
            labelId="Signer"
            id="Signer"
            label="Signer"
            aria-label="signer"
            // MUI complains that the value is initially undefined, but register seems unable to supply the initial value?
            value={form.watch('signer')}
          >
            {signatoryOptions.map(({ label, value }) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField aria-label="amount" {...form.register('amount')} label="Amount" />
          <FormControl style={{ display: 'flex', flex: 1 }}>
            <InputLabel id="Period">Period</InputLabel>
            <Select
              {...form.register('period')}
              labelId="Period"
              id="Period"
              label="Period"
              aria-label="period"
              // MUI complains that the value is initially undefined, but register seems unable to supply the initial value?
              value={form.watch('period')}
            >
              {Object.values(BackOfficePeriodV8).map((period) => (
                <MenuItem key={period} value={period}>
                  {prettifyEnumLabel(period)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        {Object.entries(form.formState.errors).map(([key, error]) => (
          <Typography key={key} variant="body2" color="error">
            {error?.message}
          </Typography>
        ))}
        <Box>
          <Button disabled={isLoading} onClick={form.handleSubmit(handleSubmit)} variant="primary">
            Create bank account
          </Button>
        </Box>
      </Box>
    </BorderContainer>
  )
}
